import type { ComponentType } from 'react';
import LineChart from '../components/charts/LineChart';
import BarChart from '../components/charts/BarChart';
import DoughnutChart from '../components/charts/DoughnutChart';
import PieChart from '../components/charts/PieChart';
import RadarChart from '../components/charts/RadarChart';
import PolarAreaChart from '../components/charts/PolarAreaChart';
import { deepMerge } from './chartHelpers';
import type { ChartDataLike } from './chartHelpers';

type ChartType = 'LINE' | 'BAR' | 'DOUGHNUT' | 'PIE' | 'RADAR' | 'POLARAREA';

const componentByType: Record<ChartType, ComponentType<any>> = {
    LINE: LineChart,
    BAR: BarChart,
    DOUGHNUT: DoughnutChart,
    PIE: PieChart,
    RADAR: RadarChart,
    POLARAREA: PolarAreaChart,
};

export const getChartComponentByType = (type: string | null | undefined): ComponentType<any> | null => {
    if (!type) return null;
    const key = String(type).toUpperCase() as ChartType;
    return componentByType[key] ?? null;
};

export const mergeChartOptions = (base: any, override: any): any =>
    deepMerge(base || {}, override || {});

export const mergeChartData = (
    base: ChartDataLike | null | undefined,
    override: ChartDataLike | null | undefined,
): ChartDataLike => {
    if (!base) return override || { datasets: [] };
    if (!override) return base;
    const baseDatasets = base.datasets || [];
    const overrideDatasets = override.datasets || [];
    const datasets = overrideDatasets.map((ds, i) => ({ ...(baseDatasets[i] || {}), ...ds }));
    return {
        ...base,
        ...override,
        labels: override.labels ?? base.labels,
        datasets: datasets.length ? datasets : baseDatasets,
    };
};

export const isCircularChartType = (type: string | null | undefined): boolean =>
    ['PIE', 'DOUGHNUT'].includes(String(type || '').toUpperCase());

export const executeChartCode = (code: string, palette: any): any => {
    // eslint-disable-next-line no-new-func
    const fn = new Function('palette', 'colors', `${code}\nreturn typeof option !== 'undefined' ? option : null;`);
    return fn(palette, palette);
};
